<template>
  <div>
    <h6 v-if="quote.switchType == 'dualConsolidatedToDualConsolidated'">
      Who is your current gas & electricity supplier?
    </h6>
    <h6 v-else-if="quote.switchType == 'gasToGas'">Who is your current gas supplier?</h6>
    <h6 v-else-if="quote.switchType == 'electricityToElectricity'">Who is your current electricity supplier?</h6>
    <h6 v-else>
      Who is your current {{ quote.editMode.prop == 'gasSupplierId' ? 'gas' : 'electricity' }}
      supplier?
    </h6>

    <div class="text-center _margin-top-20 _margin-bottom-20" v-if="loading.suppliers">
      <loader show></loader>
    </div>
    <div class="row _margin-bottom-30" v-if="!loading.suppliers">
      <div class="col-xs-12">
        <form-label id="supplier">{{ form.supplierId ? 'Your supplier' : 'Select your supplier' }}</form-label>

        <validation-messages label="Supplier" :$v="$v.form.supplierId">
          <select-list id="supplier" value-key="id" label-key="name" :list="supplierList"
            :error="$v.form.supplierId.$error" @change="onChooseSupplier" />
        </validation-messages>
      </div>
    </div>

    <div v-show="form.supplierId">
      <div class="text-center _margin-top-20 _margin-bottom-20" v-if="loading.tariffs">
        <loader show></loader>
      </div>
      <div class="form-group _margin-bottom-30" v-if="!loading.tariffs">
        <div class="col-sm-7">
          <form-label id="tariff">
            {{ form.tariff.name ? 'Your tariff' : 'Select your plan' }}
          </form-label>
          <validation-messages label="Tariff" :$v="$v.form.tariff.id">
            <select-list id="tariff" v-model="form.tariff.id" value-key="id" label-key="name" :list="tariffList"
              :error="$v.form.tariff.id.$error" @change="onChooseTariff" />
          </validation-messages>
        </div>
      </div>

      <div class="form-group" v-show="form.tariff.isFixedFromEnergyStartDate">
        <div class="col-sm-7">
          <form-label id="tariffValidToDate">Tariff End Date</form-label>
          <validation-messages label="Tariff End Date" :$v="$v.form.tariffValidToDate">
            <date-field id="tariffValidToDate" v-model="form.tariffValidToDate"
              :error="$v.form.tariffValidToDate.$error" />
          </validation-messages>
        </div>
      </div>
    </div>

    <div class="sticky-footer">
      <div class="col-xs-5">
        <uiv-btn type="link" block @click="goToQuestion('home'); resetForm()">Back</uiv-btn>
      </div>
      <div class="col-xs-7">
        <uiv-btn type="primary" block @click="onSave">Save</uiv-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mapState, mapActions } from 'vuex';
import SupplierLogo from '../../../../base/components/supplier-logo/SupplierLogo';

export default {
  name: 'SupplierTariff',
  data() {
    return {
      form: {
        supplierId: null,
        tariff: {},
        tariffValidToDate: null
      },
      requesting: false
    };
  },
  validations() {
    return {
      form: {
        supplierId: { required },
        tariff: { id: { required } },
        tariffValidToDate: { requiredIf: requiredIf(form => form.tariff.isFixedFromEnergyStartDate) }
      }
    }
  },
  computed: {
    ...mapState({
      quote: ({ quote }) => quote,
      supplierList: ({ lookups }) => lookups.suppliers,
      tariffList: ({ lookups }) => lookups.tariffs,
      loading: ({ lookups }) => lookups.requesting,
    })
  },
  watch: {
    tariffList(value) {
      const defaultTariffFound = value.find(({ isDefaultOrStandard }) => isDefaultOrStandard);

      if (defaultTariffFound) {
        this.onChooseTariff(defaultTariffFound.id);
      }
    }
  },
  methods: {
    ...mapActions({
      getTariffs: 'lookups/getTariffs',
      goToQuestion: 'quote_questions/goToQuestion'
    }),
    onChooseSupplier(id) {
      this.form.tariff = {};
      this.form.supplierId = id;
      this.form.tariffValidToDate = null;

      if (this.quote.editMode.prop == 'supplierId') {
        this.getTariffs({ fuelType: this.quote.compareFuel, supplierId: id, paymentKey: 'currentPayMethod' });
      }

      if (this.quote.editMode.prop == 'electricitySupplierId') {
        this.getTariffs({ fuelType: 'electricity', supplierId: id, paymentKey: 'electricityCurrentPayMethod' });
      }

      if (this.quote.editMode.prop == 'gasSupplierId') {
        this.getTariffs({ fuelType: 'gas', supplierId: id, paymentKey: 'gasCurrentPayMethod' });
      }
    },
    onChooseTariff(tariffId) {
      if (!tariffId) return;
      const selected = this.tariffList.find(({ id }) => id == tariffId);
      this.form.tariff = { ...selected };

      if (!selected.isFixedFromEnergyStartDate) {
        this.form.tariffValidToDate = null;
      }
    },
    onSave() {
      const form = Object.assign({}, this.form);
      const selectedSupplier = this.supplierList.find(({ id }) => id == form.supplierId);

      this.$v.form.$touch();

      if (this.$v.form.$invalid) return;

      if (this.quote.editMode.prop == 'supplierId') {
        this.quote.supplierId = form.supplierId;
        this.quote.supplierName = selectedSupplier.name;
        this.quote.tariffId = form.tariff.id;
        this.quote.tariffName = form.tariff.name;
        this.quote.tariffValidToDate = form.tariffValidToDate;
      }

      if (this.quote.editMode.prop == 'electricitySupplierId') {
        this.quote.electricitySupplierId = form.supplierId;
        this.quote.electricitySupplierName = selectedSupplier.name;
        this.quote.electricityTariffId = form.tariff.id;
        this.quote.electricityTariffName = form.tariff.name;
        this.quote.electricityTariffValidToDate = form.tariffValidToDate;
      }

      if (this.quote.editMode.prop == 'gasSupplierId') {
        this.quote.gasSupplierId = form.supplierId;
        this.quote.gasSupplierName = selectedSupplier.name;
        this.quote.gasTariffId = form.tariff.id;
        this.quote.gasTariffName = form.tariff.name;
        this.quote.gasTariffValidToDate = form.tariffValidToDate;
      }

      this.goToQuestion('home');
      this.resetForm();
    },
    resetForm() {
      this.form.supplierId = null;
      this.form.tariff = {};
      this.form.tariffValidToDate = null;
      this.$v.form.$reset();
    }
  },
  components: {
    SupplierLogo
  }
};
</script>

<style lang="scss"></style>
